import {
    Socket,
    Server
} from "net";

import {
    Subject
} from "rxjs";

import {
    TilbyTcpSocket,
    TilbyTcpSocketConnectOptions,
    TilbyTcpSocketServer,
    TilbyTcpSocketUtils
} from "./tilby-tcp-socket";

import { v4 as generateUuid } from 'uuid';

export class NodeTcpSocketServer implements TilbyTcpSocketServer {
    private server?: Server;
    private port?: number;
    private connectionSubject = new Subject<TilbyTcpSocket>();

    public onConnection = this.connectionSubject.asObservable();

    private onErrorSubject = new Subject<string>();
    public onError = this.onErrorSubject.asObservable();

    constructor() {
        const { Server } = window.require('net');
        this.server = new Server() as Server;

        this.server.on('connection', (socket) => {
            this.connectionSubject.next(new NodeTcpSocket(socket));
            NodeTcpSocketServer.logEvent(`New connection from ${socket.remoteAddress}:${socket.remotePort}`);
        });

        this.server.on('close', () => {
            NodeTcpSocketServer.logEvent(`Server closed`);
            this.connectionSubject.complete();
            this.onErrorSubject.complete();
            this.server = undefined;
        });

        this.server.on('error', (error) => {
            this.onErrorSubject.next(error.message || String(error));
            NodeTcpSocketServer.logEvent(`Server experienced a general error: ${error.message}`);
        });
    }

    private static logEvent(...message: any[]) {
        console.debug('[ NodeTcpSocketServer ]', ...message);
    }

    public listen(port: number): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.server) {
                return reject(new Error('SERVER_INSTANCE_NOT_AVAILABLE_FOR_LISTEN'));
            }

            if (this.server.listening) {
                return reject(new Error('SERVER_ALREADY_LISTENING'));
            }

            this.port = port;

            const listeningListener = () => {
                NodeTcpSocketServer.logEvent(`Listening on port ${port}`);
                cleanupListeners();
                resolve();
            };

            const errorListener = (error: any) => {
                NodeTcpSocketServer.logEvent(`Error during listen on port ${port}: ${error.message}`);
                cleanupListeners();
                reject(error);
            };

            const cleanupListeners = () => {
                this.server?.removeListener('listening', listeningListener);
                this.server?.removeListener('error', errorListener);
            };

            this.server.once('listening', listeningListener);
            this.server.once('error', errorListener);

            try {
                this.server.listen(port);
            } catch (err: any) {
                cleanupListeners();
                this.onErrorSubject.next(err.message || String(err));
                reject(err);
            }
        });
    }

    public close(): Promise<void> {
        return new Promise((resolve, reject) => {
            const serverToClose = this.server;

            if (!serverToClose) {
                NodeTcpSocketServer.logEvent(`Server was already closed or not active. Resolving close.`);
                return resolve();
            }

            const closeListener = () => {
                NodeTcpSocketServer.logEvent(`Server on port ${this.port} confirmed closed by temporary listener.`);
                cleanupListeners();
                resolve();
            };

            const errorListener = (err: any) => {
                NodeTcpSocketServer.logEvent(`Error during server.close() operation for port ${this.port}:`, err);
                cleanupListeners();
                this.onErrorSubject.next(err.message || String(err));
                reject(err instanceof Error ? err : new Error(String(err) || 'UNKNOWN_CLOSE_ERROR'));
            };

            const cleanupListeners = () => {
                serverToClose.removeListener('close', closeListener);
                serverToClose.removeListener('error', errorListener);
            };

            serverToClose.once('close', closeListener);
            serverToClose.once('error', errorListener);

            serverToClose.close();
            NodeTcpSocketServer.logEvent(`Initiated close for server on port ${this.port}.`);
        });
    }
}


export class NodeTcpSocket implements TilbyTcpSocket {
    private socket?: Socket;
    private socketId: string;

    private onDataSubject = new Subject<Uint8Array>();
    private onErrorSubject = new Subject<string>();

    public onData = this.onDataSubject.asObservable();
    public onError = this.onErrorSubject.asObservable();

    public get remoteAddress() {
        return this.socket?.remoteAddress;
    }

    public get remotePort() {
        return this.socket?.remotePort;
    }

    private static logEvent(...message: any[]) {
        console.debug('[ NodeTcpSocket ]', ...message);
    }

    constructor(socket?: Socket) {
        if (socket) {
            this.socket = socket;
        } else {
            const { Socket } = window.require('net');
            this.socket = new Socket() as Socket;
        }

        this.socketId = generateUuid();

        this.socket.on('data', (buffer) => {
            let packet = Uint8Array.from(buffer);

            NodeTcpSocket.logEvent(`Received packet from socket ${this.socketId}:`, TilbyTcpSocketUtils.ab2Hex(packet));
            this.onDataSubject.next(packet);
        });

        this.socket.on('close', () => {
            NodeTcpSocket.logEvent(`Socket ${this.socketId} closed`);
            this.socket = undefined;
            this.onDataSubject.complete();
            this.onErrorSubject.complete();
        });

        this.socket.on('error', (error) => {
            NodeTcpSocket.logEvent(`Received error from socket ${this.socketId}:`, error);
            this.onErrorSubject.next(error.message || String(error));
        });
    }

    public connect(ipAddress: string, port: number, options: TilbyTcpSocketConnectOptions): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return reject(new Error('SOCKET_NOT_OPEN'));
            }

            if (this.socket.remoteAddress && !this.socket.destroyed) {
                return reject(new Error('SOCKET_ALREADY_CONNECTED'));
            }

            let timeout = options?.timeout || 10000;

            const onConnectionSuccess = () => {
                clearTimeout(timeoutHandle);
                if (this.socket) {
                    NodeTcpSocket.logEvent(`Socket ${this.socketId} connected to ${ipAddress}:${port}`);
                    this.socket.removeListener("error", onConnectionError);
                    resolve();
                }
            }

            const onConnectionError = (error: any) => {
                clearTimeout(timeoutHandle);
                this.socket?.removeListener("connect", onConnectionSuccess);

                if (this.socket && !this.socket.destroyed) {
                    this.socket.destroy();
                }

                reject(new Error(error?.message || String(error) || 'CONNECTION_ERROR'));
            }

            const timeoutFunc = () => {
                if (this.socket && !this.socket.remoteAddress && !this.socket.destroyed) {
                    NodeTcpSocket.logEvent(`Socket ${this.socketId} connection to ${ipAddress}:${port} timed out.`);
                    clearTimeout(timeoutHandle);

                    this.socket.removeListener("connect", onConnectionSuccess);
                    this.socket.removeListener("error", onConnectionError);

                    const timeoutError = new Error('CONNECTION_TIMEOUT');
                    this.socket.destroy(timeoutError);
                    reject(timeoutError);
                }
            }

            this.socket.once("connect", onConnectionSuccess);
            this.socket.once("error", onConnectionError);

            this.socket.connect({
                host: ipAddress,
                port: port,
            });

            let timeoutHandle = setTimeout(timeoutFunc, timeout);
        })
    }

    public send(data: Uint8Array): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return reject(new Error('SOCKET_NOT_OPEN'));
            }

            this.socket.write(data, (error) => {
                if (error) {
                    NodeTcpSocket.logEvent(`Error sending packet to socket ${this.socketId}:`, error);
                    reject(new Error(error.message || String(error)));
                    this.onErrorSubject.next(error.message || String(error));
                } else {
                    NodeTcpSocket.logEvent(`Sent packet to socket ${this.socketId}:`, TilbyTcpSocketUtils.ab2Hex(data));
                    resolve();
                }
            });
        })
    }

    public disconnect(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return reject(new Error('SOCKET_NOT_OPEN'));
            }

            if (!this.socket?.remoteAddress || this.socket?.destroyed) {
                return resolve();
            }

            const disconnectListener = () => {
                NodeTcpSocket.logEvent(`Socket ${this.socketId} disconnected`);
                cleanupListeners();
                resolve();
            };

            const errorListener = (err: any) => {
                NodeTcpSocket.logEvent(`Error during disconnect of socket ${this.socketId}:`, err);
                cleanupListeners();
                this.onErrorSubject.next(err.message);
                reject(err);
            };

            const cleanupListeners = () => {
                this.socket?.removeListener('end', disconnectListener); // 'end' event signals the completion of the disconnect
                this.socket?.removeListener('error', errorListener);
            };

            this.socket.once('end', disconnectListener);
            this.socket.once('error', errorListener);

            this.socket.end();
        });
    }

    public close(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const socketToClose = this.socket;

            if (!socketToClose || socketToClose.destroyed) {
                NodeTcpSocket.logEvent(`Socket ${this.socketId || '[UNKNOWN_ID]'} is already closed or has no internal reference. Resolving close.`);
                this.socket = undefined;
                return resolve();
            }

            const closeListener = () => {
                NodeTcpSocket.logEvent(`Socket ${this.socketId} confirmed closed via event.`);
                cleanupListeners();
                this.socket = undefined;
                resolve();
            };

            const errorListener = (error: any) => {
                NodeTcpSocket.logEvent(`Error during socket close process for ${this.socketId}:`, error);
                cleanupListeners();
                this.onErrorSubject.next(error?.message || String(error));
                reject(error instanceof Error ? error : new Error(String(error) || 'UNKNOWN_CLOSE_ERROR'));
            };

            const cleanupListeners = () => {
                socketToClose.removeListener('close', closeListener);
                socketToClose.removeListener('error', errorListener);
            };

            socketToClose.once('close', closeListener);
            socketToClose.once('error', errorListener);

            try {
                if (socketToClose.remoteAddress && !socketToClose.destroyed) {
                    NodeTcpSocket.logEvent(`Socket ${this.socketId} is connected, initiating disconnect...`);
                    await this.disconnect();
                }
            } catch (error: any) {
                cleanupListeners();
                this.onErrorSubject.next(error?.message || String(error));
                return reject(error instanceof Error ? error : new Error(String(error) || 'DISCONNECT_FAILED_DURING_CLOSE'));
            }

            // If the socket is not yet destroyed (e.g., it wasn't connected or disconnect succeeded without destroying), destroy it explicitly.
            if (!socketToClose.destroyed) {
                NodeTcpSocket.logEvent(`Destroying socket ${this.socketId}...`);
                socketToClose.destroy();
            } else {
                NodeTcpSocket.logEvent(`Socket ${this.socketId} was already destroyed after disconnect attempt. Waiting for close event.`);
            }

            // Promise will be resolved/reject by the listeners above
        });
    }
}
